import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  AlertTriangle, ArrowLeft, CalendarSearch, Check, CheckCircle2, ChevronDown, ChevronUp,
  Edit3, FileUp, RefreshCw, Search, Send, UserPlus, X,
} from 'lucide-react';
import api from '../../../utils/authUtils';
import { INTEREST_CATEGORIES } from '../../../constants/doctorProfile';
import { statusMeta, SPEAKER_STATUS_KEYS, hasPendingTitleChange, hasPendingSlidesUpload, hasNotAcceptedInvite, hasNoSlidesUploaded, canInvite } from './speakerStatus';
import InviteModal from './InviteModal';
import ImportProgramModal from './ImportProgramModal';
import {
  Container, BackLink, Header, Title, Subtitle, Card, Actions, Button, Message,
  List, ListItem, Badge, RowEvent, FilterChip, ChipClear, Empty, Select, Input,
  LinkRow, StatBar, StatPill, StatPillValue, StatPillLabel, WORKSPACE, eventLabel,
} from './ui';

// A DUPLICATE candidate's assignments are what the AI found in *this*
// document - not necessarily new. Re-running detection on the same (or an
// updated) program, or a candidate whose matched profile was already
// merged, means most or all of them may already be sitting on that
// profile's own detectedAssignments. Comparing here (role + sessionTitle,
// same key the backend dedupes on - see SpeakerProfileService.
// mergeDetectedAssignments) is what stops the merge action from showing up
// - and implying something new was found - when there's nothing left to add.
const assignmentKey = a => `${a.role}::${a.sessionTitle || ''}`;
const newAssignmentsFor = (candidate, profiles) => {
  const existing = new Set((profiles.find(p => p.id === candidate.duplicateProfileId)?.detectedAssignments || []).map(assignmentKey));
  return (candidate.assignments || []).filter(a => !existing.has(assignmentKey(a)));
};

const inviteRequestFrom = profile => ({
  fullName: profile.fullName, email: profile.email, title: profile.title, specialty: profile.specialty,
  institution: profile.institution, city: profile.city, country: profile.country,
  shortBioDe: profile.shortBioDe, shortBioEn: profile.shortBioEn, photoUrl: profile.photoUrl,
  linkedInUrl: profile.linkedInUrl, facebookUrl: profile.facebookUrl, instagramUrl: profile.instagramUrl,
  websiteUrl: profile.websiteUrl, professionalStatuses: profile.professionalStatuses, interests: profile.interests,
});

// Search/browse/quick-invite. Deep editing of a profile's researched fields
// happens on the detail page (Edit); this view is for finding a speaker and
// either re-inviting a known one or jumping to a new AI research.
const SpeakerList = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const [profiles, setProfiles] = useState([]);
  const [events, setEvents] = useState([]);
  const [message, setMessage] = useState(null);
  const [inviteLink, setInviteLink] = useState('');
  const [inviting, setInviting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [startingResearchId, setStartingResearchId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [confirmingEvent, setConfirmingEvent] = useState(false);
  const [eventConfirmed, setEventConfirmed] = useState(false);
  const [mergedProfileIds, setMergedProfileIds] = useState(new Set());
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showLinked, setShowLinked] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const statusFilter = searchParams.get('status') || '';
  const titleChangesFilter = searchParams.get('titleChanges') === '1';
  const slidesFilter = searchParams.get('slidesChanges') === '1';
  const notAcceptedFilter = searchParams.get('notAccepted') === '1';
  const noSlidesFilter = searchParams.get('noSlides') === '1';
  const eventFilter = searchParams.get('event') || '';
  const topicFilter = searchParams.get('topic') || '';
  const interestLabel = interest => t(`speaker.interest.${interest}`, interest);
  const setFilter = (key, value) => { const next = new URLSearchParams(searchParams); if (value) next.set(key, value); else next.delete(key); setSearchParams(next, { replace: true }); };
  // The status/titleChanges/slidesChanges/notAccepted/noSlides filters are mutually
  // exclusive (each toggle clears the others) so the list always reads as a single
  // clear question; setExclusiveFilter centralizes that instead of repeating it per toggle.
  const setExclusiveFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    ['status', 'titleChanges', 'slidesChanges', 'notAccepted', 'noSlides'].forEach(k => next.delete(k));
    if (value) next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const clearStatusFilters = () => setExclusiveFilter();
  const toggleStatus = key => setExclusiveFilter('status', statusFilter === key ? null : key);
  const toggleTitleChanges = () => setExclusiveFilter('titleChanges', titleChangesFilter ? null : '1');
  const toggleSlidesChanges = () => setExclusiveFilter('slidesChanges', slidesFilter ? null : '1');
  const toggleNotAccepted = () => setExclusiveFilter('notAccepted', notAcceptedFilter ? null : '1');
  const toggleNoSlides = () => setExclusiveFilter('noSlides', noSlidesFilter ? null : '1');

  const load = async () => {
    try {
      const { data } = await api.get('/api/cms/speaker-profiles');
      setProfiles(data || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else setMessage({ error: true, text: t('speakerMgmt.msg.loadFailed', 'Could not load speaker drafts.') });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/api/cms/event/options').then(({ data }) => setEvents(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!profiles.some(p => ['PENDING', 'RUNNING'].includes(p.enrichmentStatus))) return undefined;
    const timer = setInterval(load, 4000); return () => clearInterval(timer);
  }, [profiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Everything about a just-finished import - see ImportProgramModal.
  const importCreated = (importResult?.candidates || []).filter(c => c.outcome === 'CREATED');
  // A CREATED draft attached to a real person who already existed (a legacy
  // User or an Account with no speaker history) rather than someone brand
  // new - see SpeakerProfileService.createDraftForExistingIdentity. Still
  // just a fuzzy name match, so call it out separately for the admin to
  // confirm before inviting, instead of folding it silently into the
  // ordinary "new speaker draft" count.
  const importLinked = importCreated.filter(c => c.linkedExistingIdentity);
  const importDuplicates = (importResult?.candidates || []).filter(c => c.outcome === 'DUPLICATE');
  const importFailed = (importResult?.candidates || []).filter(c => c.outcome === 'FAILED');
  const importedEvent = importResult?.event;
  const importFilterIds = importResult ? new Set(importCreated.map(c => c.profileId)) : null;
  const dismissImport = () => { setImportResult(null); setEventConfirmed(false); setMergedProfileIds(new Set()); setShowDuplicates(false); setShowLinked(false); setShowFailed(false); };

  const eventFilterLabel = events.find(e => String(e.id) === eventFilter);
  const statusCounts = SPEAKER_STATUS_KEYS.map(key => ({
    key,
    count: profiles.filter(p => statusMeta(p).key === key).length,
    meta: profiles.map(statusMeta).find(m => m.key === key) || { bg: '#f1f5f9', fg: '#475569', id: `speakerMgmt.status.${key}` },
  })).filter(c => c.count > 0);
  const titleChangeCount = profiles.filter(hasPendingTitleChange).length;
  const slidesChangeCount = profiles.filter(hasPendingSlidesUpload).length;
  const notAcceptedCount = profiles.filter(hasNotAcceptedInvite).length;
  const noSlidesCount = profiles.filter(hasNoSlidesUploaded).length;
  // While an import's results are showing, that filter is exclusive - a
  // distinct "just imported" mode, not composed with the other filters -
  // "Show all speakers" (dismissImport) is the way out.
  const shown = importFilterIds ? profiles.filter(p => importFilterIds.has(p.id)) : profiles.filter(p => {
    if (titleChangesFilter && !hasPendingTitleChange(p)) return false;
    if (slidesFilter && !hasPendingSlidesUpload(p)) return false;
    if (notAcceptedFilter && !hasNotAcceptedInvite(p)) return false;
    if (noSlidesFilter && !hasNoSlidesUploaded(p)) return false;
    if (statusFilter && statusMeta(p).key !== statusFilter) return false;
    if (eventFilter && !(p.engagements || []).some(e => String(e.eventId) === eventFilter)) return false;
    if (topicFilter && !(p.interests || []).includes(topicFilter)) return false;
    if (query.trim()) {
      const needle = query.trim().toLowerCase();
      if (!p.fullName?.toLowerCase().includes(needle) && !p.email?.toLowerCase().includes(needle)) return false;
    }
    return true;
  });

  // Lands the admin on exactly the people this upload created (importFilterIds
  // above), with the event-confirm control and the duplicate/failed digest in
  // a banner above the list - see docs/speaker-session-roles.md. Replaces the
  // old standalone /detect results page, which had nothing to offer beyond
  // this once "Created" rows are just normal list entries anyway.
  const onImported = async result => {
    await load();
    setImportResult(result);
    setEventConfirmed(false); setMergedProfileIds(new Set()); setShowDuplicates(false); setShowFailed(false);
    setImporting(false); setMessage(null);
  };
  const confirmEvent = async () => {
    setConfirmingEvent(true); setMessage(null);
    try {
      // Covers every profile this import actually wrote assignments onto -
      // newly created ones AND duplicates matched to an existing profile
      // (their assignments are written automatically now too - see
      // SpeakerProfileService.importOneCandidate). A returning speaker must
      // get the same one-click "ready to invite" treatment as a new one,
      // not be left out just because their profile already existed.
      const duplicateProfileIds = importDuplicates.filter(c => c.duplicateProfileId && c.assignments?.length > 0).map(c => c.duplicateProfileId);
      await api.post('/api/cms/speaker-profiles/confirm-detected-event', {
        profileIds: [...importCreated.map(c => c.profileId), ...duplicateProfileIds], eventId: importedEvent.matchedEventId,
      });
      setEventConfirmed(true);
      await load();
    } catch (err) {
      setMessage({ error: true, text: err.response?.data || t('speakerMgmt.detect.confirmEventFailed', 'Could not confirm this event match.') });
    } finally { setConfirmingEvent(false); }
  };
  // Recovers a DUPLICATE candidate's discarded assignments onto the profile
  // it matched, instead of leaving that person's newly detected sessions
  // silently lost - see SpeakerProfileService.mergeDetectedAssignments. Only
  // ever sends the assignments newAssignmentsFor found weren't already
  // there, matching exactly what the button told the admin it would do.
  const mergeDuplicate = async (candidate, newOnes) => {
    setMessage(null);
    try {
      await api.post(`/api/cms/speaker-profiles/${candidate.duplicateProfileId}/merge-detected-assignments`, {
        eventId: importedEvent?.matchedEventId || null, assignments: newOnes,
      });
      setMergedProfileIds(prev => new Set(prev).add(candidate.duplicateProfileId));
      await load();
    } catch (err) {
      setMessage({ error: true, text: err.response?.data || t('speakerMgmt.detect.mergeFailed', 'Could not merge these assignments.') });
    }
  };

  const startInvite = profile => { setMessage(null); setInviteLink(''); setInviting(profile); };
  // Starts AI research for a profile that's never had any (bulk-imported
  // drafts land here unqueued by design - see SpeakerDetectionService).
  // Same endpoint the detail page's "Retry research" button uses; reusable
  // here because a never-queued profile has no job to retry, just to start.
  const startResearch = async profile => {
    setStartingResearchId(profile.id); setMessage(null);
    try {
      await api.post(`/api/cms/speaker-profiles/${profile.id}/retry`);
      await load();
    } catch (err) { setMessage({ error: true, text: err.response?.data || t('speakerMgmt.msg.retryFailed', 'Could not retry research.') }); }
    finally { setStartingResearchId(null); }
  };
  const confirmInvite = async (eventId, assignments) => {
    setBusy(true); setMessage(null); setInviteLink('');
    try {
      const { data } = await api.post(
        `/api/cms/speaker-profiles/${inviting.id}/approve-and-invite`,
        { profile: inviteRequestFrom(inviting), resolutions: {}, eventId, assignments }
      );
      if (data.emailSent) setMessage({ text: intl.formatMessage({ id: 'speakerMgmt.invite.sent' }, { email: inviting.email }) });
      else {
        setInviteLink(`${window.location.origin}/speaker/invite?token=${data.inviteToken}`);
        setMessage({ error: true, text: intl.formatMessage({ id: 'speakerMgmt.invite.emailFailed' }) });
      }
      setInviting(null);
      await load();
    } catch (err) { setMessage({ error: true, text: err.response?.data || intl.formatMessage({ id: 'speakerMgmt.invite.error' }) }); }
    finally { setBusy(false); }
  };

  return (
    <Container>
      <BackLink to="/admin/dashboard"><ArrowLeft size={16} /><FormattedMessage id="speakerMgmt.back" defaultMessage="Back to Dashboard" /></BackLink>
      <Header>
        <Title><FormattedMessage id="speakerMgmt.list.title" defaultMessage="Speakers" /></Title>
        <Actions style={{ margin: 0 }}>
          <Button onClick={() => navigate(`${WORKSPACE}/new`)}><UserPlus size={16} /><FormattedMessage id="speakerMgmt.overview.newSpeaker" defaultMessage="Research a new speaker" /></Button>
          <Button secondary onClick={() => setImporting(true)}><FileUp size={16} /><FormattedMessage id="speakerMgmt.overview.aiExtract" defaultMessage="AI-extract speakers from program" /></Button>
          <Button secondary onClick={load}><RefreshCw size={16} /><FormattedMessage id="speakerMgmt.refresh" defaultMessage="Refresh" /></Button>
        </Actions>
      </Header>
      <Subtitle><FormattedMessage id="speakerMgmt.list.subtitle" defaultMessage="Find a speaker to invite them to another event, or open their profile to edit it." /></Subtitle>
      {message && <Message error={message.error}>{message.text}</Message>}
      {inviteLink && <Card><LinkRow><code>{inviteLink}</code><Button onClick={() => navigator.clipboard.writeText(inviteLink)}><FormattedMessage id="speakerMgmt.copy" defaultMessage="Copy" /></Button></LinkRow></Card>}

      {importResult && (
        <Card style={{ borderLeft: '4px solid #111D4E' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <strong>
              <FormattedMessage id="speakerMgmt.detect.imported" defaultMessage="{count, plural, one {# new speaker draft added} other {# new speaker drafts added}}" values={{ count: importCreated.length }} />
            </strong>
            <Button secondary onClick={dismissImport}><FormattedMessage id="speakerMgmt.import.showAll" defaultMessage="Show all speakers" /></Button>
          </div>

          {importedEvent && (importedEvent.name || importedEvent.date) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap', marginTop: '.8rem', paddingTop: '.8rem', borderTop: '1px solid #eef2f7' }}>
              <CalendarSearch size={18} color="#64748b" />
              <div style={{ flex: 1, minWidth: '14rem' }}>
                <FormattedMessage id="speakerMgmt.detect.eventGuess" defaultMessage="Detected event: {name}"
                  values={{ name: <strong>{importedEvent.name || importedEvent.date}</strong> }} />
                {importedEvent.name && importedEvent.date ? ` (${importedEvent.date})` : ''}
                {importedEvent.matchedEventId
                  ? <> — <FormattedMessage id="speakerMgmt.detect.eventMatched" defaultMessage="matched to {name} in this system" values={{ name: <strong>{importedEvent.matchedEventName}</strong> }} /></>
                  : <> — <FormattedMessage id="speakerMgmt.detect.eventUnmatched" defaultMessage="no matching event found; pick one manually when inviting" /></>}
              </div>
              {importedEvent.matchedEventId && importCreated.length > 0 && (eventConfirmed ? (
                <Badge style={{ background: '#dcfce7', color: '#166534', display: 'inline-flex', alignItems: 'center', gap: '.3rem' }}>
                  <CheckCircle2 size={13} /><FormattedMessage id="speakerMgmt.detect.eventConfirmedTag" defaultMessage="Confirmed" />
                </Badge>
              ) : (
                <Button disabled={confirmingEvent} onClick={confirmEvent}>
                  <Check size={15} /><FormattedMessage id="speakerMgmt.detect.confirmEvent" defaultMessage="Yes, this is the right event" />
                </Button>
              ))}
            </div>
          )}

          {importLinked.length > 0 && (
            <div style={{ marginTop: '.8rem', paddingTop: '.8rem', borderTop: '1px solid #eef2f7' }}>
              <button type="button" onClick={() => setShowLinked(s => !s)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: '#334155', fontWeight: 600, fontSize: '.85rem', display: 'inline-flex', alignItems: 'center', gap: '.3rem' }}>
                {showLinked ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                <FormattedMessage id="speakerMgmt.detect.linkedHeading" defaultMessage="Matched an existing person - please confirm ({count})" values={{ count: importLinked.length }} />
              </button>
              {showLinked && (
                <List style={{ marginTop: '.6rem' }}>
                  {importLinked.map((c, i) => (
                    <ListItem key={`${c.fullName}-${i}`}>
                      <div style={{ flex: 1 }}>
                        <div>{c.fullName}</div>
                        <div style={{ fontSize: '.78rem', color: '#64748b' }}>
                          <FormattedMessage id="speakerMgmt.detect.linkedNote" defaultMessage="A draft was created and attached to an existing account/user with this name - verify it's the same person before inviting." />
                        </div>
                      </div>
                      <Button secondary as={Link} to={`${WORKSPACE}/${c.profileId}`}><FormattedMessage id="speakerMgmt.list.edit" defaultMessage="View & edit" /></Button>
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          )}

          {importDuplicates.length > 0 && (
            <div style={{ marginTop: '.8rem', paddingTop: '.8rem', borderTop: '1px solid #eef2f7' }}>
              <button type="button" onClick={() => setShowDuplicates(s => !s)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: '#334155', fontWeight: 600, fontSize: '.85rem', display: 'inline-flex', alignItems: 'center', gap: '.3rem' }}>
                {showDuplicates ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                <FormattedMessage id="speakerMgmt.detect.duplicatesHeading" defaultMessage="Skipped as likely duplicates ({count})" values={{ count: importDuplicates.length }} />
              </button>
              {showDuplicates && (
                <List style={{ marginTop: '.6rem' }}>
                  {importDuplicates.map((c, i) => (
                    <ListItem key={`${c.fullName}-${i}`}>
                      <div style={{ flex: 1 }}>
                        <div>{c.fullName}</div>
                        <div style={{ fontSize: '.78rem', color: '#64748b' }}>
                          <FormattedMessage id="speakerMgmt.detect.skipped" defaultMessage="Possible match: {name}" values={{ name: c.duplicateMatchName }} />
                        </div>
                      </div>
                      {c.duplicateProfileId && <Button secondary as={Link} to={`${WORKSPACE}/${c.duplicateProfileId}`}><FormattedMessage id="speakerMgmt.list.edit" defaultMessage="View & edit" /></Button>}
                      {c.duplicateProfileId && (() => {
                        if (mergedProfileIds.has(c.duplicateProfileId)) {
                          return (
                            <Badge style={{ background: '#dcfce7', color: '#166534', display: 'inline-flex', alignItems: 'center', gap: '.3rem' }}>
                              <CheckCircle2 size={13} /><FormattedMessage id="speakerMgmt.detect.merged" defaultMessage="Merged" />
                            </Badge>
                          );
                        }
                        // Only offer the merge action when this document actually found
                        // something the matched profile doesn't already have - otherwise
                        // it wrongly reads as "a new role was found" when nothing was.
                        const newOnes = newAssignmentsFor(c, profiles);
                        return newOnes.length > 0 && (
                          <Button secondary onClick={() => mergeDuplicate(c, newOnes)}>
                            <FormattedMessage id="speakerMgmt.detect.merge" defaultMessage="Merge {count} role(s) into this profile" values={{ count: newOnes.length }} />
                          </Button>
                        );
                      })()}
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          )}

          {importFailed.length > 0 && (
            <div style={{ marginTop: '.8rem', paddingTop: '.8rem', borderTop: '1px solid #eef2f7' }}>
              <button type="button" onClick={() => setShowFailed(s => !s)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: '#991b1b', fontWeight: 600, fontSize: '.85rem', display: 'inline-flex', alignItems: 'center', gap: '.3rem' }}>
                {showFailed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                <FormattedMessage id="speakerMgmt.detect.failedHeading" defaultMessage="Failed ({count})" values={{ count: importFailed.length }} />
              </button>
              {showFailed && (
                <List style={{ marginTop: '.6rem' }}>
                  {importFailed.map((c, i) => (
                    <ListItem key={`${c.fullName}-${i}`}>
                      <div style={{ flex: 1 }}>{c.fullName}</div>
                      <Badge style={{ background: '#fee2e2', color: '#991b1b' }}>{c.errorMessage}</Badge>
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          )}
        </Card>
      )}

      {titleChangeCount > 0 && (
        <Card style={{ borderLeft: '4px solid #92400e', background: '#fffbeb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#92400e" />
              <strong>
                <FormattedMessage id="speakerMgmt.titleChanges.banner"
                  defaultMessage="{count, plural, one {# speaker has} other {# speakers have}} a pending talk-title change request."
                  values={{ count: titleChangeCount }} />
              </strong>
            </div>
            <Button onClick={toggleTitleChanges}>
              {titleChangesFilter
                ? <FormattedMessage id="speakerMgmt.titleChanges.showAll" defaultMessage="Show all speakers" />
                : <FormattedMessage id="speakerMgmt.titleChanges.review" defaultMessage="Review requests" />}
            </Button>
          </div>
        </Card>
      )}

      {slidesChangeCount > 0 && (
        <Card style={{ borderLeft: '4px solid #075985', background: '#f0f9ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#075985" />
              <strong>
                <FormattedMessage id="speakerMgmt.slidesChanges.banner"
                  defaultMessage="{count, plural, one {# speaker has} other {# speakers have}} uploaded slides you haven't reviewed yet."
                  values={{ count: slidesChangeCount }} />
              </strong>
            </div>
            <Button onClick={toggleSlidesChanges}>
              {slidesFilter
                ? <FormattedMessage id="speakerMgmt.slidesChanges.showAll" defaultMessage="Show all speakers" />
                : <FormattedMessage id="speakerMgmt.slidesChanges.review" defaultMessage="Review uploads" />}
            </Button>
          </div>
        </Card>
      )}

      {notAcceptedCount > 0 && (
        <Card style={{ borderLeft: '4px solid #5b21b6', background: '#f5f3ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#5b21b6" />
              <strong>
                <FormattedMessage id="speakerMgmt.notAccepted.banner"
                  defaultMessage="{count, plural, one {# speaker has} other {# speakers have}} not accepted their invite yet."
                  values={{ count: notAcceptedCount }} />
              </strong>
            </div>
            <Button onClick={toggleNotAccepted}>
              {notAcceptedFilter
                ? <FormattedMessage id="speakerMgmt.notAccepted.showAll" defaultMessage="Show all speakers" />
                : <FormattedMessage id="speakerMgmt.notAccepted.review" defaultMessage="Show them" />}
            </Button>
          </div>
        </Card>
      )}

      {noSlidesCount > 0 && (
        <Card style={{ borderLeft: '4px solid #b91c1c', background: '#fef2f2' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#b91c1c" />
              <strong>
                <FormattedMessage id="speakerMgmt.noSlides.banner"
                  defaultMessage="{count, plural, one {# speaker hasn't} other {# speakers haven't}} uploaded their slides yet."
                  values={{ count: noSlidesCount }} />
              </strong>
            </div>
            <Button onClick={toggleNoSlides}>
              {noSlidesFilter
                ? <FormattedMessage id="speakerMgmt.noSlides.showAll" defaultMessage="Show all speakers" />
                : <FormattedMessage id="speakerMgmt.noSlides.review" defaultMessage="Show them" />}
            </Button>
          </div>
        </Card>
      )}

      {profiles.length > 0 && (
        <StatBar>
          <StatPill type="button" $active={!statusFilter && !titleChangesFilter && !slidesFilter && !notAcceptedFilter && !noSlidesFilter} $accent="#111D4E" onClick={clearStatusFilters}>
            <StatPillValue $active={!statusFilter && !titleChangesFilter && !slidesFilter && !notAcceptedFilter && !noSlidesFilter}>{profiles.length}</StatPillValue>
            <StatPillLabel $active={!statusFilter && !titleChangesFilter && !slidesFilter && !notAcceptedFilter && !noSlidesFilter}><FormattedMessage id="speakerMgmt.overview.total" defaultMessage="All speakers" /></StatPillLabel>
          </StatPill>
          {statusCounts.map(c => (
            <StatPill key={c.key} type="button" $active={statusFilter === c.key} $accent={c.meta.fg} onClick={() => toggleStatus(c.key)}>
              <StatPillValue $active={statusFilter === c.key}>{c.count}</StatPillValue>
              <StatPillLabel $active={statusFilter === c.key}><FormattedMessage id={c.meta.id} defaultMessage={c.key} /></StatPillLabel>
            </StatPill>
          ))}
        </StatBar>
      )}

      <Card>
        <div style={{ position: 'relative', marginBottom: '.9rem' }}>
          <Search size={16} style={{ position: 'absolute', left: '.7rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <Input style={{ width: '100%', paddingLeft: '2.1rem' }} value={query} onChange={e => setFilter('q', e.target.value)}
            placeholder={t('speakerMgmt.list.searchPlaceholder', 'Search speakers by name or email…')} />
        </div>
        {eventFilter && <FilterChip><FormattedMessage id="speakerMgmt.filtered" defaultMessage="Filtered:" /> <FormattedMessage id="speakerMgmt.filter.event" defaultMessage="Event:" /> {eventFilterLabel ? eventFilterLabel.name : eventFilter}<ChipClear onClick={() => setFilter('event', '')} title={t('speakerMgmt.clearFilter', 'Clear')}><X size={13} /></ChipClear></FilterChip>}
        {topicFilter && <FilterChip><FormattedMessage id="speakerMgmt.filtered" defaultMessage="Filtered:" /> <FormattedMessage id="speakerMgmt.filter.topic" defaultMessage="Topic:" /> {interestLabel(topicFilter)}<ChipClear onClick={() => setFilter('topic', '')} title={t('speakerMgmt.clearFilter', 'Clear')}><X size={13} /></ChipClear></FilterChip>}
        <Select value={eventFilter} onChange={e => setFilter('event', e.target.value)} style={{ marginBottom: '.7rem', width: '100%' }} aria-label={t('speakerMgmt.filter.event', 'Event:')}>
          <option value="">{t('speakerMgmt.filter.eventAll', 'All events')}</option>
          {events.map(ev => <option key={ev.id} value={ev.id}>{eventLabel(ev, intl)}</option>)}
        </Select>
        <Select value={topicFilter} onChange={e => setFilter('topic', e.target.value)} style={{ marginBottom: '.7rem', width: '100%' }} aria-label={t('speakerMgmt.filter.topic', 'Topic:')}>
          <option value="">{t('speakerMgmt.filter.topicAll', 'All topics')}</option>
          {INTEREST_CATEGORIES.map(category => (
            <optgroup key={category.key} label={t(category.labelId, category.labelDefault)}>
              {category.interests.map(i => <option key={i} value={i}>{interestLabel(i)}</option>)}
            </optgroup>
          ))}
        </Select>
        {shown.length ? <List>{shown.map(profile => {
          const invitable = canInvite(profile);
          return (
            <ListItem key={profile.id}>
              <div style={{ flex: 1, minWidth: '12rem' }}>
                <Link to={`${WORKSPACE}/${profile.id}`} style={{ fontWeight: 700, color: '#111D4E', textDecoration: 'none' }}>{profile.fullName}</Link>
                <div>{profile.email || <span style={{ color: '#94a3b8' }}>{['PENDING', 'RUNNING'].includes(profile.enrichmentStatus)
                  ? <FormattedMessage id="speakerMgmt.emailPending" defaultMessage="Email pending research" />
                  : <FormattedMessage id="speakerMgmt.emailNotResearched" defaultMessage="No email yet" />}</span>}</div>
                {(() => { const m = statusMeta(profile); return <Badge style={{ background: m.bg, color: m.fg }}><FormattedMessage id={m.id} defaultMessage={m.def} /></Badge>; })()}
                {hasPendingTitleChange(profile) && <Badge style={{ background: '#fef3c7', color: '#92400e', marginLeft: '.35rem' }}><FormattedMessage id="speakerMgmt.talk.suggestionPending" defaultMessage="Title change requested" /></Badge>}
                {hasPendingSlidesUpload(profile) && <Badge style={{ background: '#e0f2fe', color: '#075985', marginLeft: '.35rem' }}><FormattedMessage id="speakerMgmt.talk.slidesPending" defaultMessage="New slides to review" /></Badge>}
                {(profile.engagements || []).length > 0 && <RowEvent>→ {[...new Set((profile.engagements || []).map(e => e.eventName))].join(' · ')}</RowEvent>}
              </div>
              <Actions style={{ margin: 0 }}>
                <Button secondary as={Link} to={`${WORKSPACE}/${profile.id}`}><Edit3 size={15} /><FormattedMessage id="speakerMgmt.list.edit" defaultMessage="View & edit" /></Button>
                {statusMeta(profile).key === 'notQueued' && (
                  <Button secondary disabled={startingResearchId === profile.id} onClick={() => startResearch(profile)}>
                    <Search size={15} />{startingResearchId === profile.id
                      ? <FormattedMessage id="speakerMgmt.list.startingResearch" defaultMessage="Starting…" />
                      : <FormattedMessage id="speakerMgmt.list.startResearch" defaultMessage="Start research" />}
                  </Button>
                )}
                <Button disabled={!invitable} title={invitable ? undefined : t('speakerMgmt.list.inviteBlocked', 'This profile still needs AI research before it can be invited.')} onClick={() => startInvite(profile)}>
                  <Send size={15} /><FormattedMessage id="speakerMgmt.list.invite" defaultMessage="Invite" />
                </Button>
              </Actions>
            </ListItem>
          );
        })}</List> : <Empty><FormattedMessage id="speakerMgmt.noDrafts" defaultMessage="No speaker drafts yet." /></Empty>}
      </Card>

      <InviteModal open={!!inviting} onClose={() => setInviting(null)} onConfirm={confirmInvite} busy={busy}
        events={events} email={inviting?.email} hasAccount={!!inviting?.accountId}
        prefillAssignments={inviting?.detectedAssignments} prefillEventId={inviting?.detectedEventId} />
      <ImportProgramModal open={importing} onClose={() => setImporting(false)} onImported={onImported} />
    </Container>
  );
};

export default SpeakerList;
